import {
  type Extension,
  StateField,
  StateEffect,
  RangeSet,
} from "@codemirror/state";
import {
  EditorView,
  Decoration,
  type DecorationSet,
  gutter,
  GutterMarker,
  WidgetType,
} from "@codemirror/view";
import type { CompileError } from "../../types/compilation";

const setErrors = StateEffect.define<CompileError[]>();

class ErrorMarker extends GutterMarker {
  severity: "error" | "warning" | "info";
  message: string;

  constructor(severity: "error" | "warning" | "info", message: string) {
    super();
    this.severity = severity;
    this.message = message;
  }

  toDOM() {
    const el = document.createElement("div");
    el.className = "lever-error-gutter-marker";
    el.title = this.message;
    el.style.cssText = `
      width: 8px; height: 8px; border-radius: 50%; cursor: pointer;
      margin: 4px 3px;
      background: ${
        this.severity === "error"
          ? "var(--color-soft-red)"
          : this.severity === "warning"
            ? "var(--color-warm-amber)"
            : "var(--color-cool-azure)"
      };
    `;
    return el;
  }
}

class ErrorLineWidget extends WidgetType {
  message: string;
  severity: "error" | "warning" | "info";

  constructor(message: string, severity: "error" | "warning" | "info") {
    super();
    this.message = message;
    this.severity = severity;
  }

  toDOM() {
    const el = document.createElement("span");
    el.className = "lever-error-line-widget";
    const color =
      this.severity === "error"
        ? "var(--color-soft-red)"
        : this.severity === "warning"
          ? "var(--color-warm-amber)"
          : "var(--color-cool-azure)";
    el.style.cssText = `
      margin-left: 12px; padding: 1px 6px; border-radius: 3px; font-size: 10px;
      background: color-mix(in srgb, ${color} 12%, transparent);
      color: ${color}; font-family: var(--font-mono); white-space: nowrap;
    `;
    el.textContent = this.message.length > 80 ? this.message.slice(0, 80) + "..." : this.message;
    return el;
  }
}

interface ErrorsByLine {
  markers: Map<number, ErrorMarker>;
  decorations: DecorationSet;
}

function computeErrorState(errors: CompileError[], doc: { lines: number; line: (n: number) => { from: number; to: number } }): ErrorsByLine {
  const markers = new Map<number, ErrorMarker>();
  const decos: { from: number; deco: Decoration }[] = [];

  for (const err of errors) {
    const lineNum = err.line;
    if (!lineNum || lineNum < 1 || lineNum > doc.lines) continue;
    if (!markers.has(lineNum)) {
      markers.set(lineNum, new ErrorMarker(err.severity as "error" | "warning" | "info", err.message));
    }
    const line = doc.line(lineNum);
    decos.push({
      from: line.to,
      deco: Decoration.widget({
        widget: new ErrorLineWidget(err.message, err.severity as "error" | "warning" | "info"),
        side: 1,
      }),
    });
  }

  decos.sort((a, b) => a.from - b.from);

  return {
    markers,
    decorations: Decoration.set(decos.map((d) => d.deco.range(d.from))),
  };
}

const errorField = StateField.define<ErrorsByLine>({
  create() {
    return { markers: new Map(), decorations: RangeSet.empty };
  },
  update(value, tr) {
    for (const effect of tr.effects) {
      if (effect.is(setErrors)) {
        return computeErrorState(effect.value, tr.state.doc);
      }
    }
    if (tr.docChanged) {
      return { markers: value.markers, decorations: value.decorations.map(tr.changes) };
    }
    return value;
  },
  provide: (field) => EditorView.decorations.from(field, (v) => v.decorations),
});

const errorGutter = gutter({
  class: "lever-error-gutter",
  markers: (view) => {
    const state = view.state.field(errorField, false);
    if (!state) return RangeSet.empty;

    const ranges: { from: number; marker: ErrorMarker }[] = [];
    for (const [lineNum, marker] of state.markers) {
      if (lineNum >= 1 && lineNum <= view.state.doc.lines) {
        const line = view.state.doc.line(lineNum);
        ranges.push({ from: line.from, marker });
      }
    }
    ranges.sort((a, b) => a.from - b.from);
    return RangeSet.of(ranges.map(({ from, marker }) => marker.range(from)));
  },
});

export function errorGutterExtension(): Extension {
  return [errorField, errorGutter];
}

export function updateErrors(view: EditorView, errors: CompileError[]) {
  view.dispatch({ effects: setErrors.of(errors) });
}
